// content-api.service.ts
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Styles } from '@app/core/interfaces/styles/styles.interface';
import { ApiUrlService } from '@app/core/services/common/api-url.service';
import { SessionStorageService } from '@app/shared/services/storage';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { PageType, SeoTag, StructureContent } from '../pages/models/content.model';

/**
 * Serwis odpowiedzialny za komunikację z API dotyczącym zarządzania treścią
 * Implementuje wzorzec fasady dla operacji związanych z blokami treści
 */
@Injectable({
  providedIn: 'root',
})
export class ContentApiService {
  private apiUrl: string;
  private lang = 'pl';

  constructor(
    private httpClient: HttpClient,
    private apiUrlService: ApiUrlService,
    private storageService: SessionStorageService,
  ) {
    this.apiUrl = this.apiUrlService.getApiUrl();
    this.lang = this.storageService.getItem('language') || 'pl';
  }

  /**
   * Pobiera strukturę bloków treści dla konkretnej strony
   * @param pageId - ID strony
   * @param clientId - Identyfikator klienta
   * @returns Observable z tablicą bloków treści
   * @remarks Wymaga istnienia client_id w localStorage
   */
  getBlockContentFromApi(pageId: number, clientId: string): Observable<StructureContent> {
    const token = localStorage.getItem('adminAuthToken');
    const headers = new HttpHeaders({
      'ignore-camel-case-interceptor': '1',
      'Authorization': `Bearer ${token}`,
    });

    const params = new HttpParams()
      .set('client_id', clientId);

    return this.httpClient.get<StructureContent>(
      `${this.apiUrl}client/creator/structure/page/${pageId}?no-cache`,
      {params, headers},
    ).pipe(
      catchError(error => {
        this.redirectIfForbidden(error);
        return throwError(() => error);
      }),
    );
  }


  /**
   * Pobiera strukturę bloków na podstawie ID bloku
   * @param blockId - Unikalny identyfikator bloku
   * @param clientId - Identyfikator klienta
   * @param token - Token autoryzacyjny JWT
   * @returns Observable z tablicą powiązanych bloków
   * @remarks Używa nowego endpointu z blockId w ścieżce URL
   */
  getBlocksByBlockIdFromApi(blockId: string, clientId: string): Observable<StructureContent> {
    const token = localStorage.getItem('adminAuthToken');
    const headers = new HttpHeaders({
      'ignore-camel-case-interceptor': '1',
      'Authorization': `Bearer ${token}`,
    });

    const params = new HttpParams()
      .set('client_id', clientId);

    return this.httpClient.get<StructureContent>(
      `${this.apiUrl}client/creator/structure/block/${blockId}?no-cache`,
      {params, headers},
    ).pipe(
      catchError(error => {
        this.redirectIfForbidden(error);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Pobiera globalne style z API
   * @returns Observable z tablicą globalnych stylów
   */
  getGlobalStyles(): Observable<{
    title: string;
    style_id: string;
    styles: Styles;
  }[]> {
    const token = localStorage.getItem('adminAuthToken');
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'ignore-camel-case-interceptor': '1',
    });

    return this.httpClient.get<{
      title: string;
      style_id: string;
      styles: Styles;
    }[]>(
      `${this.apiUrl}clients/creator/global-styles`,
      {headers},
    ).pipe(
      catchError(error => {
        console.error('Błąd pobierania globalnych stylów:', error);
        this.redirectIfForbidden(error);
        return throwError(() => error);
      }),
    );
  }


  /**
   * Wysyła zmodyfikowaną strukturę treści do API
   * @param structure - Struktura treści do zapisania
   * @param clientId - Identyfikator klienta
   * @returns Observable z wynikiem operacji
   * @throws Błąd przy braku client_id
   * @remarks Wymaga tokenu autoryzacyjnego admina
   */
  sendStructureToApi(structure: StructureContent, clientId: string): Observable<any> {
    console.log('[ContentSynchronizationService->sendStructureToApi]', [structure, clientId]);
    debugger;
    const token = localStorage.getItem('adminAuthToken');
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    });

    if (!clientId) {
      return throwError(() => new Error('Brak client_id'));
    }

    // Dynamiczny wybór endpointu i body
    const isPage = structure.structure_type === 'page';
    const basePath = isPage
      ? 'client/creator/structure/page'
      : 'client/creator/structure/block';

    const url = `${this.apiUrl}${basePath}/${structure.id}`;
    // const params = new HttpParams().set('client_id', clientId);

    // Przygotowanie body
    const body = isPage
      ? structure // Cały obiekt dla stron
      : {
        structure: structure.structure,
      }; // Tylko bloki dla bloków

    return this.httpClient.post(url, body, {headers}).pipe(
      catchError(error => {
        console.error('Błąd zapisu:', error);
        this.redirectIfForbidden(error);
        return throwError(() => error);
      }),
    );
  }


  cancel(): Observable<void> {
    const token = localStorage.getItem('adminAuthToken');

    // Sprawdzenie istnienia tokena
    if (!token) {
      return throwError(() => new Error('Brak tokena autoryzacyjnego'));
    }

    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    });

    return this.httpClient.post<void>(
      `${this.apiUrl}client/creator/cancel`,
      null,
      {headers},
    ).pipe(
      catchError(error => {
        // Automatyczne przekierowanie przy 403, 401 z #edit
        this.redirectIfForbidden(error);

        // Rzucamy błąd dla komponentu
        return throwError(() => ({
          status: error.status,
          message: error.error?.message || 'Nieznany błąd',
        }));
      }),
    );
  }

  getPageTypes(): Observable<PageType[]> {
    const token = localStorage.getItem('adminAuthToken');

    if (!token) {
      return throwError(() => new Error('Brak tokena autoryzacyjnego'));
    }

    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'ignore-camel-case-interceptor': '1',
    });

    return this.httpClient.get<PageType[]>(
      `${this.apiUrl}client/creator/page-types`,
      {headers},
    ).pipe(
      catchError(error => {
        this.redirectIfForbidden(error);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Pobiera tablicę tagów SEO z API
   * @returns Observable z tablicą SeoTag
   */
  getSeoTags(): Observable<SeoTag[]> {
    const token = localStorage.getItem('adminAuthToken');
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
    });

    return this.httpClient.get<SeoTag[]>(
      `${this.apiUrl}client/creator/dictionary/meta-tags`,
      {headers},
    ).pipe(
      catchError(error => {
        this.redirectIfForbidden(error);
        return throwError(() => error);
      }),
    );
  }

  redirectIfForbidden(error: any): never {
    // Sprawdź czy wystąpił błąd 403 LUB adres zawiera #edit
    if ((error?.status === 403 || error?.status === 401) && window.location.href.includes('#edit')) {
      // Usuń WSZYSTKIE wystąpienia #edit z URL (nawet w środku ścieżki)
      const url = window.location.href.replace(/#edit/g, '');
      // Przekieruj nawet jeśli URL się nie zmienił (np. z /page#edit na /page)
      window.location.href = url.split('#')[0]; // Dodatkowe zabezpieczenie przed fragmentami
    }
    throw error;
  }

}
